/**
 * Repères de notes sur un graphique Recharts : zones, marqueurs et infobulle.
 *
 * Le mécanisme n'existait que dans `PerformanceChart`, celui des dashboards de régies. Les
 * graphiques Google Analytics, Matomo et Piano n'en avaient aucune trace : une note cochée
 * « afficher sur les graphiques » et rattachée à une propriété analytique ne s'affichait
 * nulle part, sans que rien ne le signale.
 *
 * Il vit ici plutôt que recopié quatre fois : le regroupement par période selon la
 * granularité, le chevauchement des plages et le dédoublonnage sont trop subtils pour
 * survivre à une copie.
 */

import React, { useState, useCallback } from 'react';
import { ReferenceArea, ReferenceLine } from 'recharts';
import { sujetDeNote, couleurDeNote } from '../../constants/noteTopics';

const NoteMarkerLabel = ({ viewBox, color, noteCount, onMouseEnter, onMouseLeave }) => {
  if (!viewBox) return null;

  const { x, y, width } = viewBox;
  const centerX = x + (width ? width / 2 : 0);
  const markerY = y + 20; // Position en haut du graphique
  const hasMultiple = noteCount > 1;

  return (
    <g
      style={{ cursor: 'pointer', pointerEvents: 'all' }}
      onMouseEnter={(e) => {
        e.stopPropagation();
        onMouseEnter && onMouseEnter(e.nativeEvent || e);
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        onMouseLeave && onMouseLeave();
      }}
    >
      {/* Zone de hit plus large pour faciliter le survol */}
      <circle
        cx={centerX}
        cy={markerY}
        r={16}
        fill="transparent"
        style={{ pointerEvents: 'all' }}
      />
      {/* Cercle de fond visible */}
      <circle
        cx={centerX}
        cy={markerY}
        r={hasMultiple ? 12 : 10}
        fill={color || '#f59e0b'}
        fillOpacity={0.9}
        stroke="#fff"
        strokeWidth={2}
        style={{ pointerEvents: 'none' }}
      />
      {/* Nombre de notes ou icône */}
      <text
        x={centerX}
        y={markerY + (hasMultiple ? 5 : 4)}
        textAnchor="middle"
        fill="#fff"
        fontSize={hasMultiple ? 12 : 11}
        fontWeight={600}
        style={{ pointerEvents: 'none' }}
      >
        {hasMultiple ? noteCount : '✎'}
      </text>
    </g>
  );
};

/**
 * Une date hors du graphique n'a pas de période, mais de quel côté elle sort compte : une
 * note qui commence avant et finit après **recouvre** le graphique et doit s'y voir, alors
 * qu'une note entièrement passée ne doit pas. Rendre `null` dans les deux cas confondait les
 * deux situations.
 */
const AVANT_LE_GRAPHIQUE = '\u0000avant';
const APRES_LE_GRAPHIQUE = '\u0000apres';

// Fonction utilitaire pour déterminer la période d'une date selon la granularité
const getNotePeriodKey = (dateStr, granularity, chartDates) => {
  if (!dateStr) return null;
  const noteDate = dateStr.split('T')[0];
  const date = new Date(noteDate);

  switch (granularity) {
    case 'week': {
      // Trouver la semaine correspondante dans chartDates
      for (const chartDate of chartDates) {
        // Extraire la date de début de semaine (format "Sem. YYYY-MM-DD" ou "YYYY-MM-DD")
        const weekStartStr = chartDate.includes('Sem.')
          ? chartDate.replace('Sem. ', '')
          : chartDate;
        const weekStartDate = new Date(weekStartStr);
        const weekEndDate = new Date(weekStartDate);
        weekEndDate.setDate(weekEndDate.getDate() + 6);

        if (date >= weekStartDate && date <= weekEndDate) {
          return chartDate;
        }
      }
      /**
       * Aucune semaine affichée ne contient cette date : dire de quel côté elle sort.
       *
       * Le repli précédent — `find(dStr >= noteDate)` — rendait la **première** semaine pour
       * toute date passée, exactement le défaut que le cas `day` avait déjà corrigé plus bas.
       * Trois notes de février, mars et avril venaient ainsi se coller à la semaine du
       * 21 septembre, présentées comme si elles y tombaient.
       */
      const debutPremiere = chartDates[0].replace('Sem. ', '');
      if (noteDate < debutPremiere) return AVANT_LE_GRAPHIQUE;
      return APRES_LE_GRAPHIQUE;
    }
    case 'month': {
      // Format dans chartDates: "MM/YYYY" (ex: "07/2026")
      const monthLabel = `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      // Aussi supporter l'ancien format "YYYY-MM"
      const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return chartDates.find(d => d === monthLabel || d.startsWith(yearMonth)) || null;
    }
    case 'quarter': {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      // Format dans chartDates: "YYYY-Q3" (ex: "2026-Q3")
      const yearQuarter = `${date.getFullYear()}-Q${quarter}`;
      return chartDates.find(d => d === yearQuarter || d.includes(yearQuarter)) || null;
    }
    case 'year': {
      // Format dans chartDates: "YYYY" (ex: "2026")
      const year = date.getFullYear().toString();
      return chartDates.find(d => d === year || d.startsWith(year)) || null;
    }
    default: // 'day'
      if (chartDates.includes(noteDate)) return noteDate;
      // Le repli `find(d >= noteDate)` rendait le premier jour pour **toute** date passée :
      // une note d'il y a six mois venait se coller au bord gauche, comme si elle tombait ce
      // jour-là. On dit désormais de quel côté elle sort.
      if (noteDate < chartDates[0]) return AVANT_LE_GRAPHIQUE;
      return chartDates.find(d => d >= noteDate) || APRES_LE_GRAPHIQUE;
  }
};

// Fonction pour trouver toutes les périodes chevauchées par une plage de dates
const findOverlappingPeriods = (startDate, endDate, granularity, chartDates) => {
  const brutStart = getNotePeriodKey(startDate, granularity, chartDates);
  const brutEnd = getNotePeriodKey(endDate, granularity, chartDates);

  /**
   * Une borne qui déborde du graphique est ramenée à son bord.
   *
   * Sans cela, une note du 2 au 9 sur un graphique qui s'arrête au 8 n'a pas de période pour
   * sa fin : les deux bornes revenaient nulles et **aucun repère n'était dessiné**, alors que
   * la note couvre sept des jours affichés. C'est le cas de toute note en cours à la date du
   * jour, la plus fréquente qui soit.
   *
   * Une note entièrement hors du graphique garde ses deux bornes nulles et ne s'affiche pas.
   */
  // Entièrement d'un côté ou de l'autre : rien à afficher.
  if (brutStart === AVANT_LE_GRAPHIQUE && brutEnd === AVANT_LE_GRAPHIQUE) return { start: null, end: null };
  if (brutStart === APRES_LE_GRAPHIQUE && brutEnd === APRES_LE_GRAPHIQUE) return { start: null, end: null };

  const auBord = (cle, repli) => (
    cle === AVANT_LE_GRAPHIQUE ? chartDates[0]
      : cle === APRES_LE_GRAPHIQUE ? chartDates[chartDates.length - 1]
        : cle || repli
  );
  const start = auBord(brutStart, brutEnd ? chartDates[0] : null);
  const end = auBord(brutEnd, brutStart ? chartDates[chartDates.length - 1] : null);

  if (!start || !end) return { start: null, end: null };

  // Trouver les indices
  const startIdx = chartDates.indexOf(start);
  const endIdx = chartDates.indexOf(end);

  if (startIdx === -1 || endIdx === -1) {
    return { start, end };
  }

  // Retourner les bornes correctes (start peut être > end si les dates sont inversées)
  return {
    start: chartDates[Math.min(startIdx, endIdx)],
    end: chartDates[Math.max(startIdx, endIdx)]
  };
};

/**
 * Les éléments Recharts à insérer parmi les enfants du graphique : zones colorées pour les
 * notes à plage, marqueurs groupés par période pour toutes.
 *
 * @param {object[]} notes notes de la période, telles que les rend l'API
 * @param {object[]} donneesDuGraphique les points du graphique, portant `date`
 * @param {string} granularite `day`, `week`, `month`, `quarter` ou `year`
 * @param {Function} onSurvol reçoit `({ notes, periodKey }, evenement)`
 * @param {Function} onSortie
 */
export function marqueursDeNotes({ notes, donneesDuGraphique, granularite = 'day', periode = null, onSurvol, onSortie }) {
  // Récupérer toutes les dates du graphique
const chartDates = donneesDuGraphique?.map(d => d.date) || [];
const chartDatesSet = new Set(chartDates);
const minChartDate = chartDates.length > 0 ? chartDates[0] : null;
const maxChartDate = chartDates.length > 0 ? chartDates[chartDates.length - 1] : null;

if (!notes || !minChartDate || !maxChartDate) return null;

/**
 * La période affichée borne les notes, et l'étiquette des colonnes ne suffit pas à le faire.
 *
 * Aux granularités larges, une colonne porte le nom de sa **période nominale** : « 2026 »
 * couvre l'année entière alors que le graphique n'en montre parfois qu'une semaine. Une note de
 * février s'y rattachait donc légitimement du point de vue de l'étiquette, et faussement du
 * point de vue du lecteur, qui regarde sept jours de septembre.
 *
 * `periode` est facultative : les graphiques qui reçoivent des données **déjà agrégées** ne
 * connaissent plus les bornes réelles et ne peuvent pas la fournir. Ils gardent alors le
 * comportement antérieur, borné par les seules étiquettes.
 */
const dansLaPeriode = (note) => {
  if (!periode?.debut || !periode?.fin) return true;
  const debut = (note.startDate || '').split('T')[0];
  if (!debut) return false;
  const fin = note.endDate ? note.endDate.split('T')[0] : debut;
  // Chevauchement, et non inclusion : une note commencée avant et finie pendant compte.
  return debut <= periode.fin && fin >= periode.debut;
};

const notesToShow = notes.filter(n => n.showOnChart && dansLaPeriode(n));
if (notesToShow.length === 0) return null;

// Grouper TOUTES les notes par période selon la granularité
// Les notes avec plage sont ajoutées à CHAQUE période qu'elles chevauchent
const notesByPeriod = {};
const rangeNotes = []; // Pour les zones colorées uniquement

notesToShow.forEach((note) => {
  const noteStart = note.startDate ? note.startDate.split('T')[0] : null;
  const noteEnd = note.endDate ? note.endDate.split('T')[0] : null;

  if (!noteStart) return;

  // Vérifier si la note chevauche la période du graphique
  // Utiliser getNotePeriodKey pour mapper les dates aux périodes du graphique
  const startPeriodKey = getNotePeriodKey(noteStart, granularite, chartDates);
  const endPeriodKey = noteEnd ? getNotePeriodKey(noteEnd, granularite, chartDates) : startPeriodKey;

  // Si aucune période ne correspond, la note est hors de la plage du graphique
  if (!startPeriodKey && !endPeriodKey) return;

  if (noteEnd && noteEnd !== noteStart) {
    // Note avec plage de dates
    rangeNotes.push(note);

    // Trouver toutes les périodes chevauchées
    const { start: startPeriod, end: endPeriod } = findOverlappingPeriods(
      noteStart,
      noteEnd,
      granularite,
      chartDates
    );

    if (startPeriod && endPeriod) {
      const startIdx = chartDates.indexOf(startPeriod);
      const endIdx = chartDates.indexOf(endPeriod);

      if (startIdx !== -1 && endIdx !== -1) {
        if (granularite === 'day') {
          // En vue jour : UN seul marqueur au début de la plage + zone colorée
          const periodKey = chartDates[Math.min(startIdx, endIdx)];
          if (!notesByPeriod[periodKey]) {
            notesByPeriod[periodKey] = [];
          }
          if (!notesByPeriod[periodKey].some(n => n.id === note.id)) {
            notesByPeriod[periodKey].push(note);
          }
        } else {
          // Pour semaine/mois/trimestre/année : ajouter la note à CHAQUE période chevauchée
          for (let i = Math.min(startIdx, endIdx); i <= Math.max(startIdx, endIdx); i++) {
            const periodKey = chartDates[i];
            if (!notesByPeriod[periodKey]) {
              notesByPeriod[periodKey] = [];
            }
            // Éviter les doublons
            if (!notesByPeriod[periodKey].some(n => n.id === note.id)) {
              notesByPeriod[periodKey].push(note);
            }
          }
        }
      }
    }
  } else {
    // Note avec date unique
    const periodKey = getNotePeriodKey(noteStart, granularite, chartDates);
    if (periodKey && chartDatesSet.has(periodKey)) {
      if (!notesByPeriod[periodKey]) {
        notesByPeriod[periodKey] = [];
      }
      notesByPeriod[periodKey].push(note);
    }
  }
});

const elements = [];

// Afficher les zones colorées pour les notes avec plage de dates
rangeNotes.forEach((note, idx) => {
  const noteStart = note.startDate.split('T')[0];
  const noteEnd = note.endDate.split('T')[0];

  // Trouver les périodes de début et fin selon la granularité
  const { start: effectiveStart, end: effectiveEnd } = findOverlappingPeriods(
    noteStart,
    noteEnd,
    granularite,
    chartDates
  );

  if (!effectiveStart || !effectiveEnd) return;

  elements.push(
    <ReferenceArea
      key={`note-area-${idx}`}
      x1={effectiveStart}
      x2={effectiveEnd}
      yAxisId="left"
      fill={couleurDeNote(note)}
      fillOpacity={0.15}
      stroke="none"
    />
  );
});

// Afficher les marqueurs groupés par période (toutes les notes confondues)
Object.entries(notesByPeriod).forEach(([periodKey, periodNotes]) => {
  // Utiliser la couleur de la première note ou orange par défaut
  const primaryColor = periodNotes[0] ? couleurDeNote(periodNotes[0]) : '#f59e0b';

  elements.push(
    <ReferenceLine
      key={`note-group-${periodKey}`}
      x={periodKey}
      yAxisId="left"
      stroke={primaryColor}
      strokeWidth={2}
      strokeDasharray="5 5"
      label={<NoteMarkerLabel
        color={primaryColor}
        noteCount={periodNotes.length}
        onMouseEnter={(e) => {
          onSurvol({ notes: periodNotes, periodKey }, e);
          
        }}
        onMouseLeave={() => onSortie()}
      />}
    />
  );
});

  return elements;
}

/**
 * L'état de survol d'un marqueur, avec ses deux gestionnaires.
 *
 * Sorti dans un hook pour qu'aucun graphique n'ait à redéclarer les mêmes états ni la même
 * position de souris.
 */
export function useNotesDeGraphique() {
  const [survolee, setSurvolee] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const onSurvol = useCallback((groupe, evenement) => {
    setSurvolee(groupe);
    setPosition({ x: evenement.clientX, y: evenement.clientY });
  }, []);
  const onSortie = useCallback(() => setSurvolee(null), []);

  return { survolee, position, onSurvol, onSortie };
}

/** L'infobulle d'un groupe de notes, en position fixe et hors du graphique. */
export const InfobulleDeNotes = ({ survolee, position }) => {
  if (!survolee || !survolee.notes) return null;

  return (

  <div
    style={{
      position: 'fixed',
      left: Math.min(position.x + 15, window.innerWidth - 330),
      top: Math.max(position.y - 10, 10),
      backgroundColor: 'white',
      border: `2px solid ${survolee.notes[0] ? couleurDeNote(survolee.notes[0]) : '#f59e0b'}`,
      borderRadius: '8px',
      padding: '12px 16px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 1000,
      maxWidth: '320px',
      maxHeight: '400px',
      overflowY: 'auto',
      pointerEvents: 'none'
    }}
  >
    {/* En-tête avec le nombre de notes */}
    {survolee.notes.length > 1 && (
      <div style={{
        fontSize: '11px',
        color: '#666',
        marginBottom: '8px',
        paddingBottom: '6px',
        borderBottom: '1px solid #eee'
      }}>
        📝 {survolee.notes.length} notes sur cette période
      </div>
    )}

    {/* Liste des notes */}
    {survolee.notes.map((note, idx) => (
      <div
        key={idx}
        style={{
          marginBottom: idx < survolee.notes.length - 1 ? '12px' : 0,
          paddingBottom: idx < survolee.notes.length - 1 ? '12px' : 0,
          borderBottom: idx < survolee.notes.length - 1 ? '1px solid #eee' : 'none'
        }}
      >
        <div style={{
          fontWeight: 600,
          marginBottom: '4px',
          color: couleurDeNote(note),
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: couleurDeNote(note),
            flexShrink: 0
          }} />
          {sujetDeNote(note.topic) && (
            <span style={{
              padding: '1px 7px',
              borderRadius: '20px',
              fontSize: '10px',
              fontWeight: 500,
              color: sujetDeNote(note.topic).couleur,
              backgroundColor: sujetDeNote(note.topic).fond,
              whiteSpace: 'nowrap'
            }}>
              {sujetDeNote(note.topic).label}
            </span>
          )}
          {note.startDate?.split('T')[0]}
          {note.endDate && note.endDate !== note.startDate &&
            ` → ${note.endDate.split('T')[0]}`}
        </div>
        <div style={{
          fontSize: '13px',
          color: '#333',
          lineHeight: '1.4',
          whiteSpace: 'pre-wrap',
          marginLeft: '14px'
        }}>
          {typeof note.content === 'string'
            ? note.content
            : note.content?.text || note.content?.content || ''}
        </div>
      </div>
    ))}
  </div>

  );
};
